import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SearchResults = ({ searchQuery, filters, onStockSelect, onAddToWatchlist }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

  // Mock search results
  const mockResults = [
    {
      symbol: 'AAPL',
      name: 'Apple Inc.',
      price: 189.25,
      change: 2.45,
      changePercent: 1.31,
      volume: '52.3M',
      marketCap: '2.95T',
      sector: 'Technology',
      peRatio: 28.5,
      dividendYield: 0.52,
      logo: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=100&h=100&fit=crop&crop=center'
    },
    {
      symbol: 'GOOGL',
      name: 'Alphabet Inc.',
      price: 2847.50,
      change: -15.30,
      changePercent: -0.53,
      volume: '28.9M',
      marketCap: '1.75T',
      sector: 'Technology',
      peRatio: 24.8,
      dividendYield: 0.00,
      logo: 'https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?w=100&h=100&fit=crop&crop=center'
    },
    {
      symbol: 'MSFT',
      name: 'Microsoft Corporation',
      price: 378.90,
      change: 5.67,
      changePercent: 1.52,
      volume: '31.4M',
      marketCap: '2.81T',
      sector: 'Technology',
      peRatio: 32.1,
      dividendYield: 0.68,
      logo: 'https://images.unsplash.com/photo-1633419461186-7d40a38105ec?w=100&h=100&fit=crop&crop=center'
    },
    {
      symbol: 'TSLA',
      name: 'Tesla Inc.',
      price: 245.60,
      change: -8.90,
      changePercent: -3.50,
      volume: '89.2M',
      marketCap: '780.5B',
      sector: 'Consumer Discretionary',
      peRatio: 65.2,
      dividendYield: 0.00,
      logo: 'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=100&h=100&fit=crop&crop=center'
    }
  ];

  useEffect(() => {
    if (searchQuery || Object.values(filters).some(f => f !== '' && f !== null)) {
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        let filteredResults = [...mockResults];
        
        if (searchQuery) {
          filteredResults = filteredResults.filter(stock =>
            stock.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
            stock.name.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }

        // Apply filters
        if (filters.sector) {
          filteredResults = filteredResults.filter(stock =>
            stock.sector.toLowerCase().includes(filters.sector.toLowerCase())
          );
        }

        if (filters.priceRange.min) {
          filteredResults = filteredResults.filter(stock =>
            stock.price >= parseFloat(filters.priceRange.min)
          );
        }

        if (filters.priceRange.max) {
          filteredResults = filteredResults.filter(stock =>
            stock.price <= parseFloat(filters.priceRange.max)
          );
        }

        setResults(filteredResults);
        setLoading(false);
      }, 500);
    } else {
      setResults([]);
    }
  }, [searchQuery, filters]);

  const handleSort = (criteria) => {
    setSortBy(criteria);
    const sorted = [...results].sort((a, b) => {
      switch (criteria) {
        case 'price-high':
          return b.price - a.price;
        case 'price-low':
          return a.price - b.price;
        case 'change-high':
          return b.changePercent - a.changePercent;
        case 'change-low':
          return a.changePercent - b.changePercent;
        case 'volume':
          return parseFloat(b.volume) - parseFloat(a.volume);
        default:
          return 0;
      }
    });
    setResults(sorted);
  };

  const handleAddToWatchlist = (e, stock) => {
    e.stopPropagation();
    onAddToWatchlist(stock);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
          <span className="ml-3 text-muted-foreground">Searching stocks...</span>
        </div>
      </div>
    );
  }

  if (!searchQuery && !Object.values(filters).some(f => f !== '' && f !== null)) {
    return (
      <div className="text-center py-12">
        <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">Start Your Search</h3>
        <p className="text-muted-foreground">
          Enter a stock symbol or company name to discover investment opportunities
        </p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="SearchX" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No Results Found</h3>
        <p className="text-muted-foreground">
          Try adjusting your search terms or filters to find more stocks
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Results Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            Search Results ({results.length})
          </h2>
          {searchQuery && (
            <p className="text-sm text-muted-foreground mt-1">
              Results for "{searchQuery}"
            </p>
          )}
        </div>
        
        <div className="flex items-center space-x-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-muted rounded-lg p-1">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <Icon name="List" size={16} />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Icon name="Grid3X3" size={16} />
            </Button>
          </div>

          {/* Sort Dropdown */}
          <select
            value={sortBy}
            onChange={(e) => handleSort(e.target.value)}
            className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="relevance">Sort by Relevance</option>
            <option value="price-high">Price: High to Low</option>
            <option value="price-low">Price: Low to High</option>
            <option value="change-high">Change: High to Low</option>
            <option value="change-low">Change: Low to High</option>
            <option value="volume">Volume</option>
          </select>
        </div>
      </div>

      {/* Results Grid/List */}
      <div className={`
        ${viewMode === 'grid' ?'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' :'space-y-4'
        }
      `}>
        {results.map((stock) => (
          <div
            key={stock.symbol}
            onClick={() => onStockSelect(stock)}
            className={`
              bg-card border border-border rounded-lg p-4 cursor-pointer
              hover:shadow-hover transition-all duration-200 hover:-translate-y-1
              ${viewMode === 'list' ? 'flex items-center justify-between' : ''}
            `}
          >
            {viewMode === 'grid' ? (
              // Grid View
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center overflow-hidden">
                      <img
                        src={stock.logo}
                        alt={stock.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm" style={{ display: 'none' }}>
                        {stock.symbol.charAt(0)}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{stock.symbol}</h3>
                      <p className="text-sm text-muted-foreground">{stock.sector}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => handleAddToWatchlist(e, stock)}
                    className="h-8 w-8 hover:bg-primary/10"
                  >
                    <Icon name="Plus" size={16} />
                  </Button>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground truncate">{stock.name}</p>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold font-mono text-foreground">
                    ₹{stock.price.toFixed(2)}
                  </span>
                  <div className={`text-right ${stock.change >= 0 ? 'text-success' : 'text-destructive'}`}>
                    <div className="font-mono text-sm">
                      {stock.change >= 0 ? '+' : ''}{stock.change.toFixed(2)}
                    </div>
                    <div className="font-mono text-xs">
                      ({stock.change >= 0 ? '+' : ''}{stock.changePercent.toFixed(2)}%)
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                  <div>
                    <span className="block">Volume</span>
                    <span className="font-mono">{stock.volume}</span>
                  </div>
                  <div>
                    <span className="block">Market Cap</span>
                    <span className="font-mono">{stock.marketCap}</span>
                  </div>
                </div>
              </div>
            ) : (
              // List View
              <>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center overflow-hidden">
                    <img
                      src={stock.logo}
                      alt={stock.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-primary/10 flex items-center justify-center text-primary font-semibold" style={{ display: 'none' }}>
                      {stock.symbol.charAt(0)}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground">{stock.symbol}</h3>
                    <p className="text-sm text-muted-foreground">{stock.name}</p>
                    <p className="text-xs text-muted-foreground">{stock.sector}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <div className="font-mono text-lg font-semibold text-foreground">
                      ${stock.price.toFixed(2)}
                    </div>
                    <div className={`font-mono text-sm ${stock.change >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {stock.change >= 0 ? '+' : ''}{stock.change.toFixed(2)} ({stock.change >= 0 ? '+' : ''}{stock.changePercent.toFixed(2)}%)
                    </div>
                  </div>

                  <div className="text-right text-sm text-muted-foreground">
                    <div>Vol: {stock.volume}</div>
                    <div>Cap: {stock.marketCap}</div>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => handleAddToWatchlist(e, stock)}
                    className="h-8 w-8 hover:bg-primary/10"
                  >
                    <Icon name="Plus" size={16} />
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SearchResults;